import { useState } from "react";
import { Check, Copy, Globe, Linkedin, Mail, Phone, Printer, Scale } from "lucide-react";
import {
  useGetIdentity,
  useLocaleState,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { DeleteButton } from "@/components/admin/delete-button";
import { ShowButton } from "@/components/admin/show-button";
import { TextField } from "@/components/admin/text-field";
import { UrlField } from "@/components/admin/url-field";
import { SelectField } from "@/components/admin/select-field";

import { formatLocalizedDate } from "../misc/RelativeDate";
import { AsideSection } from "../misc/AsideSection";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company } from "../types";
import { getTranslatedCompanySizeLabel } from "./getTranslatedCompanySizeLabel";
import { sizes } from "./sizes";
import { getStateName } from "./states";
import { useGetSalesName } from "../sales/useGetSalesName";

interface CompanyAsideProps {
  link?: string;
}

export const CompanyAside = ({ link = "edit" }: CompanyAsideProps) => {
  const record = useRecordContext<Company>();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <div className="hidden sm:block w-92 min-w-92 space-y-4">
      <div className="flex flex-row space-x-1">
        {link === "edit" ? (
          <EditButton label={translate("resources.companies.action.edit")} />
        ) : (
          <ShowButton label={translate("resources.companies.action.show")} />
        )}
      </div>

      <CompanyInfo record={record} />

      {record.sector === "Amtsgericht" ? <AmtsgerichtContactInfo record={record} /> : null}

      <AddressInfo record={record} />

      <ContextInfo record={record} />

      <AdditionalInfo record={record} />

      {link !== "edit" && (
        <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
          <DeleteButton
            className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
            size="sm"
          />
        </div>
      )}
    </div>
  );
};

export const CompanyInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  if (
    !record.website &&
    !record.linkedin_url &&
    !record.phone_number &&
    !record.telefax
  ) {
    return null;
  }

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.contact")}
    >
      {record.website && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Globe className="w-4 h-4" />
          <UrlField
            source="website"
            target="_blank"
            rel="noopener"
            content={record.website
              .replace("http://", "")
              .replace("https://", "")}
          />
        </div>
      )}
      {record.linkedin_url && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Linkedin className="w-4 h-4" />
          <a
            className="underline hover:no-underline"
            href={record.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            title={record.linkedin_url}
          >
            LinkedIn
          </a>
        </div>
      )}
      {record.phone_number && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Phone className="w-4 h-4" />
          <TextField source="phone_number" />
        </div>
      )}
      {record.telefax && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Printer className="w-4 h-4" />
          <span className="text-sm">Fax: </span>
          <TextField source="telefax" />
        </div>
      )}
    </AsideSection>
  );
};

export const ContextInfo = ({ record }: { record: Company }) => {
  const { companySectors } = useConfigurationContext();
  const translate = useTranslate();
  if (!record.revenue && !record.id) {
    return null;
  }

  const sector = companySectors.find((s) => s.value === record.sector);
  const sectorLabel = sector?.label;
  const translatedSizes = sizes.map((size) => ({
    ...size,
    name: getTranslatedCompanySizeLabel(size, translate),
  }));

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.context")}
    >
      {sectorLabel && (
        <span>
          {translate("resources.companies.fields.sector")}: {sectorLabel}
        </span>
      )}
      {record.size && (
        <span>
          {translate("resources.companies.fields.size")}:{" "}
          <SelectField source="size" choices={translatedSizes} />
        </span>
      )}
      {record.revenue && (
        <span>
          {translate("resources.companies.fields.revenue")}:{" "}
          <TextField source="revenue" />
        </span>
      )}
      {record.tax_identifier && (
        <span>
          {translate("resources.companies.fields.tax_identifier", {})}
          : <TextField source="tax_identifier" />
        </span>
      )}
    </AsideSection>
  );
};

export const AddressInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  if (
    !record.address &&
    !record.city &&
    !record.zipcode &&
    !record.state_abbr
  ) {
    return null;
  }

  const isAg = record.sector === "Amtsgericht";
  const stateName = getStateName(record.state_abbr);
  const country = record.country || "Deutschland";

  // Brief-Block-Lines: was kopiert wird (Name, Abteilung, Straße, PLZ+Ort)
  const copyLines: string[] = [];
  if (isAg) {
    if (record.name) copyLines.push(record.name);
    if (record.abteilung)
      copyLines.push("Abteilung für Zwangsversteigerung");
  }
  if (record.address) copyLines.push(record.address);
  const plzOrt = [record.zipcode, record.city].filter(Boolean).join(" ");
  if (plzOrt) copyLines.push(plzOrt);

  // Display-Lines: copyLines + Bundesland + Land (zur Info, NICHT in Copy)
  const displayLines = [...copyLines];
  if (stateName) displayLines.push(stateName);
  if (country) displayLines.push(country);

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.address")}
      noGap
    >
      <div className="flex flex-col gap-1">
        {displayLines.map((line, i) => (
          <span key={i} className="text-sm">
            {line}
          </span>
        ))}
        <CopyAddressPill lines={copyLines} />
      </div>
    </AsideSection>
  );
};

const CopyAddressPill = ({ lines }: { lines: string[] }) => {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard failed silently
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-1 inline-flex items-center self-start gap-1 px-2 py-0.5 rounded-full border text-xs hover:bg-muted transition-colors"
      aria-label="Anschrift kopieren"
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      <span>{copied ? "Kopiert" : "Anschrift kopieren"}</span>
    </button>
  );
};

export const AdditionalInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  const [locale = "en"] = useLocaleState();
  const { identity } = useGetIdentity();
  const isCurrentUser = record.sales_id === identity?.id;
  const salesName = useGetSalesName(record.sales_id, {
    enabled: !isCurrentUser,
  });
  if (
    !record.created_at &&
    !record.sales_id &&
    !record.description &&
    !record.context_links
  ) {
    return null;
  }
  const getBaseURL = (url: string) => {
    const urlObject = new URL(url.startsWith("http") ? url : `https://${url}`);
    return urlObject.hostname;
  };

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.additional_info")}
    >
      {record.description && (
        <p className="text-sm  mb-1">{record.description}</p>
      )}
      {record.context_links && (
        <div className="flex flex-col">
          {record.context_links.map((link, index) =>
            link ? (
              <a
                key={index}
                className="text-sm underline hover:no-underline mb-1"
                href={link.startsWith("http") ? link : `https://${link}`}
                target="_blank"
                rel="noopener noreferrer"
                title={link}
              >
                {getBaseURL(link)}
              </a>
            ) : null,
          )}
        </div>
      )}
      {record.sales_id !== null && (
        <div className="inline-flex text-sm text-muted-foreground mb-1">
          {translate(
            isCurrentUser
              ? "resources.companies.followed_by_you"
              : "resources.companies.followed_by",
            { name: salesName },
          )}
        </div>
      )}
      {record.created_at && (
        <p className="text-sm text-muted-foreground mb-1">
          {translate("resources.companies.added_on", {
            date: formatLocalizedDate(record.created_at, locale),
          })}{" "}
        </p>
      )}
    </AsideSection>
  );
};


export const AmtsgerichtContactInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  if (
    !record.email &&
    !record.telefon_2 &&
    !record.telefon_3 &&
    !record.postanschrift
  ) {
    return null;
  }
  return (
    <AsideSection title="Amtsgericht-Kontakt">
      {record.email ? (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Mail className="w-4 h-4" />
          <a
            className="underline hover:no-underline text-sm"
            href={`mailto:${record.email}`}
          >
            {record.email}
          </a>
        </div>
      ) : null}
      {record.telefon_2 ? (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Phone className="w-4 h-4" />
          <span className="text-sm">
            {record.telefon_2}
            {record.telefon_2_notiz ? (
              <span className="text-muted-foreground ml-1">
                · {record.telefon_2_notiz}
              </span>
            ) : null}
          </span>
        </div>
      ) : null}
      {record.telefon_3 ? (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Phone className="w-4 h-4" />
          <span className="text-sm">
            {record.telefon_3}
            {record.telefon_3_notiz ? (
              <span className="text-muted-foreground ml-1">
                · {record.telefon_3_notiz}
              </span>
            ) : null}
          </span>
        </div>
      ) : null}
      {record.postanschrift && record.postanschrift !== record.address ? (
        <div className="text-xs text-muted-foreground mt-2">
          <span className="font-medium">Postanschrift: </span>
          <span
            className="whitespace-pre-line"
            dangerouslySetInnerHTML={{
              __html: record.postanschrift.replace(/<br\s*\/?>/g, "\n"),
            }}
          />
        </div>
      ) : null}
      {record.biethinweise_link ? (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Scale className="w-4 h-4" />
          <a
            className="underline hover:no-underline text-sm"
            href={record.biethinweise_link}
            target="_blank"
            rel="noopener noreferrer"
          >
            Biethinweis (PDF)
          </a>
        </div>
      ) : null}
      {record.abteilung ? (
        <div className="text-xs text-muted-foreground italic mt-1">
          {record.abteilung}
        </div>
      ) : null}
    </AsideSection>
  );
};
